import { Card } from "./Card";
import { BoltIcon, CheckIcon } from "./Icons";
import { formatBytes } from "../flasher/binaryInfo";
import type { FlashProgress } from "../flasher/flashService";
import type { FlashState } from "../hooks/useFlasher";
import type { FlashPhase } from "../flasher/errors";

interface Props {
  flashState: FlashState;
  progress: FlashProgress | null;
  canFlash: boolean;
  connected: boolean;
  partCount: number;
  totalBytes: number;
  blocked: boolean;
  onFlash: () => void;
}

const PHASE_LABELS: Record<FlashPhase, string> = {
  "select-port": "Choosing a port",
  connect: "Connecting",
  erase: "Erasing flash",
  write: "Writing",
  verify: "Verifying",
  reset: "Resetting board",
  monitor: "Monitoring",
};

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "--";
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.ceil(seconds % 60)}s`;
}

export function FlashPanel({
  flashState,
  progress,
  canFlash,
  connected,
  partCount,
  totalBytes,
  blocked,
  onFlash,
}: Props) {
  const flashing = flashState === "flashing";
  // Erase reports no byte progress, so the bar sweeps instead of filling.
  const indeterminate = flashing && (progress === null || progress.phase === "erase");
  const percent = progress ? Math.min(100, Math.round(progress.overall * 100)) : 0;

  return (
    <Card
      step={4}
      done={flashState === "success"}
      title="Flash"
      hint={
        partCount > 0
          ? `${partCount} file${partCount === 1 ? "" : "s"} selected, ${formatBytes(totalBytes)} to write.`
          : "Add firmware files to continue."
      }
    >
      {flashing && (
        <div className="progress">
          <div className="progress__top">
            <span className="progress__label">
              {progress ? PHASE_LABELS[progress.phase] : "Preparing"}
              {progress && progress.phase === "write" && progress.fileName ? ` — ${progress.fileName}` : ""}
            </span>
            <span className="progress__percent">{indeterminate ? "…" : `${percent}%`}</span>
          </div>

          <div className="bar">
            <div
              className={`bar__fill${indeterminate ? " bar__fill--indeterminate" : ""}`}
              style={{ width: `${percent}%` }}
            />
          </div>

          {progress && progress.phase === "write" && (
            <div className="stats">
              <div className="stat">
                <div className="stat__label">Written</div>
                <div className="stat__value">{formatBytes(progress.bytesWritten)}</div>
              </div>
              <div className="stat">
                <div className="stat__label">Total</div>
                <div className="stat__value">{formatBytes(progress.bytesTotal)}</div>
              </div>
              <div className="stat">
                <div className="stat__label">Speed</div>
                <div className="stat__value">
                  {progress.speed ? `${(progress.speed / 1024).toFixed(0)} KB/s` : "--"}
                </div>
              </div>
              <div className="stat">
                <div className="stat__label">Remaining</div>
                <div className="stat__value">
                  {progress.etaSeconds !== null ? formatDuration(progress.etaSeconds) : "--"}
                </div>
              </div>
            </div>
          )}

          <p className="muted">Do not unplug the board or close this tab until the write finishes.</p>
        </div>
      )}

      {flashState === "success" && !flashing && (
        <div className="notice notice--success">
          <span className="notice__icon">
            <CheckIcon size={16} />
          </span>
          <div className="notice__body">
            <div className="notice__title">Firmware written and verified</div>
            Every region was read back and checksummed against your files. The board has been reset and is running
            the new firmware.
          </div>
        </div>
      )}

      <button className="btn btn--primary btn--block" onClick={onFlash} disabled={!canFlash}>
        <BoltIcon size={18} />
        {flashing ? "Flashing..." : flashState === "success" ? "Flash again" : "Flash firmware"}
      </button>

      {!canFlash && !flashing && (
        <p className="muted">
          {!connected
            ? "Connect a board first."
            : partCount === 0
              ? "Add at least one firmware file."
              : blocked
                ? "Resolve the errors listed above before flashing."
                : ""}
        </p>
      )}
    </Card>
  );
}
